/**
 * RW-269 — Per-user daily feature analytics logger.
 *
 * Fires on every route change. Writes to users/{uid}/analytics/{YYYY-MM-DD}
 * using setDoc(merge:true) + increment() to avoid read-before-write.
 *
 * Also stamps lastLoginAt + lastActiveFeature on the user doc.
 *
 * Cost: ~2 Firestore writes per page view. At 195 agents × 20 views/day
 * = ~7,800 writes/day — well within free tier.
 */
import { doc, setDoc, increment, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { withTenant } from '../services/data';

// Route → feature mapping. Exported for src/utils/analyticsLogger.routes.test.js,
// which pins the /deals entries AND that every value here is a key both adoption
// surfaces already know (RW-2313).
export const ROUTE_TO_FEATURE = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/feed': 'feed',
  '/home': 'feed',
  '/contacts': 'contacts',
  '/listings': 'listings',
  '/buyers': 'buyers',
  /* RW-2313: the Deals-side modules. These routes have NEVER been in this map,
     so every visit to them logged as 'other' — which is the real and only
     reason the `coming_soon` adoption row has read zero, NOT the RW-1524 kill
     of the MODULE_ENTERED emitter (that feeds the top-level `analytics`
     collection, which the adoption dashboards do not read for features).
     RW-1541 folded Transactions into One Pipeline, so /deals IS `transactions`.
     Every key here must exist in BOTH AdoptionDashboard.js (FEATURE_LABELS +
     HEATMAP_FEATURES) and functions-scheduled adoptionMetrics.js (FEATURES +
     FEATURE_LABELS) — another paired registry; `buyer_needs` was added to all
     four by RW-2313.

     RW-2443: /deals KEEPS the stored key `transactions` — both adoption
     surfaces now LABEL it "Deals". Renaming the key would split every stored
     users/{uid}/analytics doc (and MODULES.TRANSACTIONS in the MODULE_ENTERED
     stream) into a before and an after. The Deals screens below used to log
     'other', which is why nobody could say whether the One Pipeline flip
     worked; the deal record itself is the DEALS_SCREENS rule further down. */
  '/deals': 'transactions',
  '/deals/tasks': 'deal_tasks',
  '/deals/docs': 'deal_docs',
  '/deals/settings': 'deal_settings',
  '/deals/review': 'deal_review',
  '/connect/coming-soon': 'coming_soon', // RW-3647: was /deals/coming-soon
  '/connect/buyer-needs': 'buyer_needs', // RW-3647: was /deals/buyer-needs
  '/focus': 'focus_mode',
  '/leaderboard': 'leaderboard',
  '/training': 'training',
  '/events': 'events',
  '/people': 'directory',
  '/org-chart': 'directory',
  '/offices': 'directory',
  '/ninja-flow': 'ninja_flow',
  '/documents': 'document_library',
  '/settings': 'settings',
  '/messages': 'messages',
  '/my-tasks': 'my_tasks',
  '/my-production': 'my_production',
  '/calendar': 'calendar',
  '/onboarding': 'onboarding',
  '/photos': 'photos',
  '/vendors': 'vendors',
  '/saved': 'saved_posts',
  '/search': 'search',
  '/notifications': 'notifications',
  '/groups': 'groups',
};

/* RW-2443: every one-segment /deals/<x> that App.js renders as a SCREEN
   (renderDealsScreen). App.js renders <DealDetail> for
   any OTHER /^\/deals\/[^/]+$/, so that is what this logger counts as
   `deal_record`. A screen missing from this list would be counted as a deal
   record, so analyticsLogger.routes.test.js reads App.js and fails unless the
   two lists match exactly. Screens with their own key are also in
   ROUTE_TO_FEATURE; the rest (home, new, chat, payouts, commissions, people,
   leadership, roster) deliberately stay 'other' — see that test. */
export const DEALS_SCREENS = [
  'tasks', 'home', 'new', 'chat', 'payouts', 'commissions', 'review', 'people',
  'leadership', 'roster', 'docs', 'settings',
  // RW-3647: the two Market boards moved to /connect/*. Their old /deals/*
  // paths are redirects that App.js neither renders nor logs.
];
const DEAL_RECORD_RE = /^\/deals\/([^/]+)$/;

/* Prefix match for detail pages, first match wins. A table rather than an if
   chain so the pairing guard (RW-2443) can check every feature it can produce
   against both adoption surfaces — the exact map alone was checked before, so
   a prefix key could reach neither list and nothing noticed.
   RW-2313: an emailed / notified link opens the RECORD, never the board, so
   without the two /deals/… rows a deep-link visit is the module's only traffic
   and still reads as 'other'. Same key as the board — there is no separate
   detail key registered for either module, and inventing one would need four
   more registry edits for a distinction nothing asks for. */
export const PREFIX_TO_FEATURE = [
  ['/connect/coming-soon/', 'coming_soon'], // RW-3647: was /deals/coming-soon/
  ['/connect/buyer-needs/', 'buyer_needs'],
  ['/listings/', 'listing_detail'],
  ['/buyers/', 'buyer_detail'],
  ['/contacts/', 'contact_detail'],
  ['/training/', 'training_course'],
  ['/events/', 'event_detail'],
  ['/people/', 'profile'],
  ['/admin', 'admin'],
  ['/documents/', 'document_library'],
];

export function getFeatureFromPath(path) {
  if (ROUTE_TO_FEATURE[path]) return ROUTE_TO_FEATURE[path];

  const record = DEAL_RECORD_RE.exec(path);
  if (record && !DEALS_SCREENS.includes(record[1])) return 'deal_record';

  const prefix = PREFIX_TO_FEATURE.find(([p]) => path.startsWith(p));
  if (prefix) return prefix[1];

  return 'other';
}

/**
 * Log a page view. Call on every route change.
 * Writes to a daily summary doc to minimize Firestore writes.
 * Silent — never breaks the app.
 */
export async function logPageView(userId, path) {
  if (!userId || !path) return;

  const feature = getFeatureFromPath(path);
  const today = new Date().toISOString().split('T')[0];

  try {
    const dailyRef = doc(db, 'users', userId, 'analytics', today);

    await setDoc(dailyRef, withTenant(`users/${userId}/analytics`, {
      date: today,
      userId,
      totalViews: increment(1),
      features: arrayUnion(feature),
      [`featureCounts.${feature}`]: increment(1),
      lastActivity: serverTimestamp(),
      lastFeature: feature,
    }), { merge: true });

    // Also update user doc with last login timestamp
    await setDoc(doc(db, 'users', userId), withTenant('users', {
      lastLoginAt: serverTimestamp(),
      lastActiveFeature: feature,
    }), { merge: true });
  } catch (err) {
    // Silent fail — analytics should never break the app
    console.warn('Analytics log failed:', err.message);
  }
}
